(function () {
  if (window.__readSirahLandingScriptLoaded) return;
  window.__readSirahLandingScriptLoaded = true;

  let revealObserver;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;

    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    return revealObserver;
  }

  function initReveals() {
    const observer = getRevealObserver();

    document.querySelectorAll('.reveal').forEach((node) => {
      if (node.dataset.rsRevealObserved === 'true') return;
      node.dataset.rsRevealObserved = 'true';
      observer.observe(node);
    });
  }

  // Pause CSS infinite animations when their element scrolls off-screen
  // so the marquee + scroll-cue stop burning CPU/battery on long pages.
  function initOffscreenAnimations() {
    const targets = document.querySelectorAll('.marquee-track, .scroll-cue-line');
    if (!targets.length || typeof IntersectionObserver !== 'function') return;

    const animObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
        });
      },
      { threshold: 0 }
    );

    targets.forEach((node) => {
      if (node.dataset.rsAnimObserved === 'true') return;
      node.dataset.rsAnimObserved = 'true';
      animObserver.observe(node);
    });
  }

  function initWaitlistForms() {
    const forms = document.querySelectorAll('[data-waitlist-form], #WaitlistForm');

    forms.forEach((form) => {
      if (form.dataset.rsWaitlistInitialized === 'true') return;
      form.dataset.rsWaitlistInitialized = 'true';

      const submitBtn = form.querySelector('[data-waitlist-submit], .signup-submit');
      const defaultBtnText = submitBtn ? submitBtn.textContent : 'Join our family';
      const joinTarget = form.dataset.joinTarget || 'join';
      const canonicalJoinUrl =
        form.dataset.canonicalJoinUrl || `${window.location.pathname}#${joinTarget}`;
      const joinSection = document.getElementById(joinTarget);

      const normalizeToJoinUrl = () => {
        window.history.replaceState({}, '', canonicalJoinUrl);
      };

      const ensureJoinVisible = () => {
        if (joinSection) {
          joinSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      const getMessageNode = () => {
        let node = form.querySelector('[data-waitlist-message]');
        if (!node) {
          node = document.createElement('p');
          node.className = 'form-message is-empty';
          node.setAttribute('data-waitlist-message', '');
          node.setAttribute('role', 'status');
          node.setAttribute('aria-live', 'polite');
          form.appendChild(node);
        }
        return node;
      };

      const root = form.closest('[data-waitlist-root]');
      const inputRow = root && root.querySelector('[data-waitlist-input-row]');
      const microcopy = root && root.querySelector('[data-waitlist-microcopy]');
      const successCard = root && root.querySelector('[data-waitlist-success]');

      const showSuccessCard = () => {
        if (!successCard) return;
        if (inputRow) inputRow.hidden = true;
        if (microcopy) microcopy.hidden = true;
        const msgNode = form.querySelector('[data-waitlist-message]');
        if (msgNode) {
          msgNode.className = 'form-message is-empty';
          msgNode.textContent = '';
        }
        successCard.hidden = false;
        successCard.classList.add('is-visible');
        if (typeof successCard.focus === 'function') {
          successCard.setAttribute('tabindex', '-1');
          successCard.focus({ preventScroll: true });
        }
      };

      const setMessage = (type, text) => {
        if (type === 'success') {
          showSuccessCard();
          return;
        }

        const node = getMessageNode();

        if (!text) {
          node.className = 'form-message is-empty';
          node.textContent = '';
          node.setAttribute('role', 'status');
          node.setAttribute('aria-live', 'polite');
          return;
        }

        node.className = `form-message ${type}`;
        if (type === 'error') {
          node.setAttribute('role', 'alert');
          node.setAttribute('aria-live', 'assertive');
        } else {
          node.setAttribute('role', 'status');
          node.setAttribute('aria-live', 'polite');
        }
        node.textContent = text;
      };

      const composeWaitlistTags = () => {
        const tagsInput = form.querySelector('[data-waitlist-tags]');
        if (!tagsInput) return;

        const base = tagsInput.dataset.baseTags || 'waitlist';
        const selected = form.querySelector('input[name="preferred_format_ui"]:checked');
        tagsInput.value = selected ? `${base},preferred_format_${selected.value}` : base;
      };

      const searchParams = new URLSearchParams(window.location.search);
      const hasLegacyCustomerQuery =
        searchParams.get('form_type') === 'customer' &&
        (searchParams.has('customer_posted') || searchParams.has('contact[tags]'));
      const isWaitlistMarker = searchParams.get('waitlist') === '1';

      const preRenderedSuccess = form.querySelector('[data-waitlist-message].success');

      if ((isWaitlistMarker || hasLegacyCustomerQuery || preRenderedSuccess) && form.dataset.rsWaitlistQueryHandled !== 'true') {
        setMessage('success', "You're on the list — Thank you.");
        normalizeToJoinUrl();
        ensureJoinVisible();
        form.dataset.rsWaitlistQueryHandled = 'true';
      }

      let isSubmitting = false;

      const resolveActionUrl = () => {
        try {
          const url = new URL(form.action, window.location.href);
          if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
          return url;
        } catch {
          return null;
        }
      };

      form.addEventListener(
        'submit',
        async (event) => {
          event.preventDefault();
          composeWaitlistTags();

          if (isSubmitting) return;

          const actionUrl = resolveActionUrl();
          if (!actionUrl) {
            setMessage('error', 'Something went wrong. Try again or email us directly.');
            return;
          }

          isSubmitting = true;

          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Joining…';
          }
          setMessage('', '');

          try {
            const response = await fetch(actionUrl.href, {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: new FormData(form),
              credentials: 'same-origin',
            });

            const data = await response.json().catch(() => null);
            const ok = (data && data.status === 200) || response.ok;

            if (ok) {
              setMessage('success', "You're on the list — Thank you.");
              form.reset();
              composeWaitlistTags();
              normalizeToJoinUrl();
              ensureJoinVisible();
            } else {
              setMessage('error', 'Something went wrong. Try again or email us directly.');
            }
          } catch {
            setMessage('error', 'Something went wrong. Try again or email us directly.');
          } finally {
            isSubmitting = false;
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = defaultBtnText;
            }
          }
        },
        { capture: true }
      );
    });
  }

  function initShopCarousels() {
    document.querySelectorAll('[data-shop-carousel]').forEach((carousel) => {
      if (carousel.dataset.rsShopCarouselInitialized === 'true') return;
      carousel.dataset.rsShopCarouselInitialized = 'true';

      const grid = carousel.querySelector('.shop-grid');
      const prev = carousel.querySelector('[data-shop-carousel-prev]');
      const next = carousel.querySelector('[data-shop-carousel-next]');
      if (!grid || !prev || !next) return;

      const cardWidth = () => {
        const card = grid.querySelector('.book-card');
        if (!card) return 320;
        const gap = parseFloat(getComputedStyle(grid).gap) || 0;
        return card.offsetWidth + gap;
      };

      const updateState = () => {
        const hasOverflow = grid.scrollWidth > grid.clientWidth + 1;
        carousel.classList.toggle('has-overflow', hasOverflow);
        const atStart = grid.scrollLeft <= 1;
        const atEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
        prev.toggleAttribute('disabled', atStart);
        next.toggleAttribute('disabled', atEnd);
      };

      prev.addEventListener('click', () => {
        grid.scrollBy({ left: -cardWidth(), behavior: 'smooth' });
      });
      next.addEventListener('click', () => {
        grid.scrollBy({ left: cardWidth(), behavior: 'smooth' });
      });

      grid.addEventListener('scroll', updateState, { passive: true });
      window.addEventListener('resize', updateState);
      // Re-measure once images load (their width affects scrollWidth)
      grid.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', updateState, { once: true });
      });

      updateState();
    });
  }

  function initShopAddToCart() {
    document.querySelectorAll('[data-shop-action="cart"]').forEach((btn) => {
      if (btn.dataset.rsShopAtcBound === 'true') return;
      btn.dataset.rsShopAtcBound = 'true';

      const form = btn.closest('form');
      if (!form) return;

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (btn.disabled) return;

        const drawer = document.querySelector('cart-drawer');
        const formData = new FormData(form);
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          const sectionIds = drawer.getSectionsToRender().map((s) => s.id);
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });
          const data = await response.json();

          if (data.status) {
            btn.textContent = 'Try again';
            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1800);
            return;
          }

          if (drawer && typeof drawer.renderContents === 'function') {
            if (typeof drawer.setActiveElement === 'function') {
              drawer.setActiveElement(btn);
            }
            // product-form.js:104 pattern — `is-empty` lives on <cart-drawer>
            // itself; renderContents won't clear it, so the drawer keeps
            // showing the empty state even after the new line item arrives.
            drawer.classList.remove('is-empty');
            if (data.sections) {
              drawer.renderContents(data);
            } else {
              const fallback = await fetch('/?sections=cart-drawer,cart-icon-bubble').then((r) => r.json()).catch(() => null);
              if (fallback) {
                drawer.renderContents({ sections: fallback });
              } else if (typeof drawer.open === 'function') {
                drawer.open();
              }
            }
          } else if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          } else {
            window.location.href = '/cart';
            return;
          }

          btn.textContent = 'Added';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 1500);
        } catch (err) {
          btn.textContent = originalText;
          btn.disabled = false;
        }
      });
    });
  }

  // Sticky CTA — reveal once the user has scrolled past the hero, hide
  // again when the waitlist section is on screen (no need for the bar
  // when the form is right there).
  function initStickyCta() {
    const cta = document.querySelector('[data-rs-sticky-cta]');
    const hero = document.querySelector('.hero');
    const join = document.querySelector('#join');
    if (!cta || !hero) return;

    cta.removeAttribute('hidden');

    const update = () => {
      const heroBottom = hero.getBoundingClientRect().bottom;
      const joinTop = join ? join.getBoundingClientRect().top : Infinity;
      const past = heroBottom < 80;
      const atForm = joinTop < window.innerHeight * 0.6;
      cta.classList.toggle('is-visible', past && !atForm);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  function initLanding() {
    initReveals();
    initOffscreenAnimations();
    initWaitlistForms();
    initShopCarousels();
    initShopAddToCart();
    initStickyCta();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLanding);
  } else {
    initLanding();
  }

  document.addEventListener('shopify:section:load', initLanding);
})();
